/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {cva, VariantProps} from 'class-variance-authority';
import React, {ReactNode} from 'react';

import {cn} from '../../services/utils';

const inputVariants = cva(
  'flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-input',
        outline: 'border-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({className, type, variant, ...props}, ref) => {
  return <input type={type} className={cn(inputVariants({variant, className}))} ref={ref} {...props} />;
});
Input.displayName = 'Input';

const InputSet = ({children, className}: {children: ReactNode; className?: string}) => {
  return <div className={cn('flex flex-col gap-y-6', className)}>{children}</div>;
};

const InputItem = ({children}: {children: ReactNode}) => {
  return <div className={'flex flex-col gap-y-3'}>{children}</div>;
};

export {Input, InputSet, InputItem};
